import React, { useCallback, useEffect, useState, useRef } from 'react';
import posthog from 'posthog-js';
import { Navigation } from './components/Navigation';
import { Hero } from './components/Hero';
import { WorkSection } from './components/WorkSection';
import { Footer } from './components/Footer';
import { ProjectDetail } from './pages/ProjectDetail';
import { WebMessagingAdsGrowth } from './pages/WebMessagingAdsGrowth';
import { YohanaPersonalAssistant } from './pages/YohanaPersonalAssistant';
import { ShopsAdsTrustSystem } from './pages/ShopsAdsTrustSystem';
import { AboutPage } from './pages/AboutPage';
import { AccentsCollective } from './pages/AccentsCollective';
import { AppByPhone } from './pages/AppByPhone';
const PROJECT_SLUG_MAP: Record<string, {
  slug: string;
  page: string;
}> = {
  'Unified Guidance Framework': {
    slug: '/unified-guidance',
    page: 'unified_guidance'
  },
  'Yohana Personal Assistant': {
    slug: '/yohana',
    page: 'yohana'
  },
  'Web Messaging Ads Growth': {
    slug: '/web-messaging',
    page: 'web_messaging'
  },
  'Shop Ads Trust System': {
    slug: '/shop-ads',
    page: 'shop_ads'
  },
  'Accents Collective': {
    slug: '/accents-collective',
    page: 'accents_collective'
  },
  'App by Phone': {
    slug: '/app-by-phone',
    page: 'app_by_phone'
  }
};
const SLUG_TO_PROJECT: Record<string, string> = Object.fromEntries(Object.entries(PROJECT_SLUG_MAP).map(([title, {
  slug
}]) => [slug, title]));
function getInitialState(): {
  view: 'home' | 'about' | 'project';
  project: string | null;
} {
  const path = window.location.pathname;
  if (path === '/about') return {
    view: 'about',
    project: null
  };
  if (SLUG_TO_PROJECT[path]) return {
    view: 'project',
    project: SLUG_TO_PROJECT[path]
  };
  return {
    view: 'home',
    project: null
  };
}
export function App() {
  const initial = getInitialState();
  const [currentView, setCurrentView] = useState<'home' | 'about' | 'project'>(initial.view);
  const [currentProject, setCurrentProject] = useState<string | null>(initial.project);
  const pendingScrollToWork = useRef(false);
  const isPopState = useRef(false);
  // Track page views and update URL
  useEffect(() => {
    let pageName: string;
    let urlPath: string;
    if (currentView === 'home') {
      pageName = 'home';
      urlPath = '/home';
    } else if (currentView === 'about') {
      pageName = 'about';
      urlPath = '/about';
    } else if (currentView === 'project' && currentProject) {
      const mapping = PROJECT_SLUG_MAP[currentProject];
      if (mapping) {
        pageName = mapping.page;
        urlPath = mapping.slug;
      } else {
        pageName = currentProject.toLowerCase().replace(/\s+/g, '_');
        urlPath = '/' + currentProject.toLowerCase().replace(/\s+/g, '-');
      }
    } else {
      return;
    }
    posthog.capture('page_view', {
      page: pageName
    });
    if (!isPopState.current) {
      window.history.pushState({
        view: currentView,
        project: currentProject
      }, '', urlPath);
    }
    isPopState.current = false;
  }, [currentView, currentProject]);
  // Handle browser back/forward
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      isPopState.current = true;
      if (event.state) {
        setCurrentView(event.state.view || 'home');
        setCurrentProject(event.state.project || null);
      } else {
        const {
          view,
          project
        } = getInitialState();
        setCurrentView(view);
        setCurrentProject(project);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);
  const handleHomeClick = () => {
    setCurrentView('home');
    setCurrentProject(null);
  };
  const handleWorkClick = () => {
    if (currentView === 'home') {
      document.getElementById('work')?.scrollIntoView({
        behavior: 'smooth'
      });
    } else {
      pendingScrollToWork.current = true;
      setCurrentView('home');
      setCurrentProject(null);
    }
  };
  useEffect(() => {
    if (currentView === 'home' && pendingScrollToWork.current) {
      pendingScrollToWork.current = false;
      requestAnimationFrame(() => {
        document.getElementById('work')?.scrollIntoView({
          behavior: 'smooth'
        });
      });
    }
  }, [currentView]);
  const handleAboutClick = () => {
    setCurrentView('about');
  };
  const handleProjectClick = (title: string) => {
    setCurrentProject(title);
    setCurrentView('project');
  };
  return <div className="min-h-screen bg-white selection:bg-ink selection:text-white" data-id="element-0" data-mp="zxale1wb5alh">
      <Navigation onHomeClick={handleHomeClick} onWorkClick={handleWorkClick} onAboutClick={handleAboutClick} currentView={currentView} data-id="element-1" data-mp="vwidpd333drb" />
      <main data-id="element-2" data-mp="3klyglm1xj1ku">
        {currentView === 'project' && currentProject === 'Web Messaging Ads Growth' && <WebMessagingAdsGrowth title={currentProject} data-id="element-3" data-mp="1w4f58t11llpp" />}
        {currentView === 'project' && currentProject === 'Yohana Personal Assistant' && <YohanaPersonalAssistant title={currentProject} gridLineColor="#E0E9CE" data-id="element-4" data-mp="203xmnetnnens" />}
        {currentView === 'project' && currentProject === 'Shop Ads Trust System' && <ShopsAdsTrustSystem title={currentProject} data-id="element-5" data-mp="2ev6hc2cezq7s" />}
        {currentView === 'project' && currentProject === 'Accents Collective' && <AccentsCollective title={currentProject} data-id="element-6" data-mp="2jw41gkgziubx" />}
        {currentView === 'project' && currentProject === 'App by Phone' && <AppByPhone title={currentProject} data-id="element-7" data-mp="2sv7jdl4f13ou" />}
        {currentView === 'project' && currentProject !== 'Web Messaging Ads Growth' && currentProject !== 'Yohana Personal Assistant' && currentProject !== 'Shop Ads Trust System' && currentProject !== 'Accents Collective' && currentProject !== 'App by Phone' && currentProject && <ProjectDetail title={currentProject} data-id="element-8" data-mp="1wsr1jtlha97w" />}
        {currentView === 'about' && <AboutPage data-id="element-9" data-mp="1wuu2h8o65mre" />}
        {currentView === 'home' && <>
            <Hero data-id="element-10" data-mp="2atjxmirfhcd4" />
            <WorkSection onProjectClick={handleProjectClick} data-id="element-11" data-mp="2apw4hkpwaryx" />
          </>}
      </main>
      <Footer data-id="element-12" data-mp="25vig429nhl3r" />
    </div>;
}